import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Image, Play, Eye, Sparkles, MoreVertical, Copy } from 'lucide-react';

/**
 * AdNode - Visual node for individual ad in funnel builder
 */
const AdNode = memo(({ data, selected }) => {
    const {
        name = 'New Ad',
        parentAdSetId,
        creativeId,
        creativeUrl,
        creativeThumbnail,
        isVideo = false,
        headline = '',
        primaryText = '',
        cta = 'LEARN_MORE',
        status = 'draft',
        variant, // A, B, C for A/B testing
        onEdit,
        onPreview,
        onDuplicate,
        onMenuClick
    } = data;

    const statusColors = {
        draft: 'var(--color-text-tertiary)',
        generated: 'var(--color-info)',
        approved: 'var(--color-success)',
        rejected: 'var(--color-error)'
    };

    const statusLabels = {
        draft: 'Draft',
        generated: 'AI Generated',
        approved: 'Approved',
        rejected: 'Rejected'
    };

    // CTA button labels
    const ctaLabels = {
        LEARN_MORE: 'Learn More',
        SHOP_NOW: 'Shop Now',
        SIGN_UP: 'Sign Up',
        BOOK_NOW: 'Book Now',
        CONTACT_US: 'Contact Us',
        DOWNLOAD: 'Download',
        GET_OFFER: 'Get Offer',
        GET_QUOTE: 'Get Quote'
    };

    return (
        <div
            style={{
                background: 'var(--color-bg-primary)',
                border: selected
                    ? '2px solid var(--color-success)'
                    : '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-3)',
                minWidth: '200px',
                maxWidth: '220px',
                boxShadow: selected ? 'var(--shadow-lg)' : 'var(--shadow-sm)',
                transition: 'all var(--transition-fast)'
            }}
        >
            {/* Target handle (top) - connects from Ad Set */}
            <Handle
                type="target"
                position={Position.Top}
                id="adset-in"
                style={{
                    background: 'var(--color-info)',
                    width: '10px',
                    height: '10px'
                }}
            />

            {/* Header with variant badge */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-2)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    {/* Variant badge for A/B testing */}
                    {variant && (
                        <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--color-warning)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 700
                        }}>
                            {variant}
                        </div>
                    )}
                    <div>
                        <div style={{
                            fontSize: '10px',
                            color: 'var(--color-text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            Ad {variant ? `(Variant ${variant})` : ''}
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                            maxWidth: '140px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {name}
                        </div>
                    </div>
                </div>

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onMenuClick?.();
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--color-text-tertiary)',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <MoreVertical size={14} />
                </button>
            </div>

            {/* Creative thumbnail */}
            <div style={{
                width: '100%',
                height: '100px',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                background: 'var(--color-bg-tertiary)',
                marginBottom: 'var(--space-2)',
                position: 'relative',
                cursor: 'pointer'
            }}
                onClick={(e) => {
                    e.stopPropagation();
                    onPreview?.();
                }}
            >
                {creativeThumbnail || creativeUrl ? (
                    <img
                        src={creativeThumbnail || creativeUrl}
                        alt={name}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                ) : (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        <Image size={24} />
                        <span style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-1)' }}>
                            Select Creative
                        </span>
                    </div>
                )}

                {/* Video indicator */}
                {isVideo && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(0,0,0,0.6)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Play size={16} color="white" fill="white" />
                    </div>
                )}

                {/* Preview overlay on hover */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity var(--transition-fast)'
                }}
                    className="preview-overlay"
                >
                    <Eye size={20} color="white" />
                </div>
            </div>

            {/* Headline preview */}
            {headline && (
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    marginBottom: 'var(--space-1)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {headline}
                </div>
            )}

            {/* Primary text preview */}
            {primaryText && (
                <div style={{
                    fontSize: '10px',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-2)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                }}>
                    {primaryText}
                </div>
            )}

            {/* Status and actions */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 'var(--space-2)'
            }}>
                {/* Status badge */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)'
                }}>
                    {status === 'generated' && (
                        <Sparkles size={10} style={{ color: statusColors[status] }} />
                    )}
                    <span style={{
                        fontSize: '10px',
                        color: statusColors[status],
                        fontWeight: 500
                    }}>
                        {statusLabels[status]}
                    </span>
                </div>

                {/* Duplicate button (keep for creating variants) */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate?.();
                    }}
                    title="Create variant"
                    style={{
                        background: 'none',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '2px 4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'var(--color-text-tertiary)'
                    }}
                >
                    <Copy size={10} />
                </button>
            </div>

            {/* CTA preview */}
            {cta && (
                <div style={{
                    marginTop: 'var(--space-2)',
                    padding: '4px 8px',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-sm)',
                    textAlign: 'center',
                    fontSize: '10px',
                    color: 'var(--color-text-secondary)',
                    fontWeight: 500
                }}>
                    {ctaLabels[cta] || cta}
                </div>
            )}

            <style>{`
                .preview-overlay:hover {
                    opacity: 1 !important;
                }
            `}</style>
        </div>
    );
});

AdNode.displayName = 'AdNode';

export default AdNode;
